import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface QuickLink {
  id: string;
  name: string;
  url: string;
  description?: string;
  color?: string;
}

const STORAGE_KEY = 'skeena_quicklinks';

// Farben fuer die Kacheln
const TILE_COLORS = [
  '#1976d2', // Blue
  '#388e3c', // Green
  '#d32f2f', // Red
  '#7b1fa2', // Purple
  '#f57c00', // Orange
  '#0288d1', // Light Blue
  '#c2185b', // Pink
  '#455a64', // Blue Grey
  '#5d4037', // Brown
  '#00796b', // Teal
];

// Favicon URL generieren
const getFaviconUrl = (url: string) => {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return null;
  }
};

// Sortable Link Card Component
interface SortableLinkCardProps {
  link: QuickLink;
  onView: (link: QuickLink) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, linkId: string) => void;
  isDragging?: boolean;
}

const SortableLinkCard: React.FC<SortableLinkCardProps> = ({ link, onView, onMenuClick, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: link.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)', lg: 'calc(25% - 18px)' },
        minWidth: 250,
      }}
    >
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          transition: 'transform 0.2s, box-shadow 0.2s',
          cursor: isDragging ? 'grabbing' : 'default',
          '&:hover': {
            transform: isDragging ? 'none' : 'translateY(-4px)',
            boxShadow: isDragging ? 2 : 4,
          },
        }}
      >
        {/* Drag Handle */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
            cursor: 'grab',
            bgcolor: 'rgba(255,255,255,0.9)',
            borderRadius: 1,
            p: 0.25,
            display: 'flex',
            alignItems: 'center',
            '&:hover': { bgcolor: 'white' },
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Box>

        <CardActionArea
          onClick={() => onView(link)}
          sx={{ height: '100%', minHeight: 180 }}
        >
          <Box
            sx={{
              bgcolor: link.color || TILE_COLORS[0],
              height: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getFaviconUrl(link.url) ? (
              <Box
                component="img"
                src={getFaviconUrl(link.url)!}
                alt=""
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'white',
                  borderRadius: 1,
                  p: 0.5,
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <OpenInNewIcon sx={{ fontSize: 48, color: 'white' }} />
            )}
          </Box>
          <CardContent>
            <Typography variant="h6" noWrap>
              {link.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {new URL(link.url).hostname}
            </Typography>
            {link.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {link.description}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>

        {/* Menu Button */}
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'white' },
          }}
          onClick={(e) => onMenuClick(e, link.id)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Card>
    </Box>
  );
};

// Drag Overlay Card (shown while dragging)
const DragOverlayCard: React.FC<{ link: QuickLink }> = ({ link }) => (
  <Card
    sx={{
      width: 280,
      height: 180,
      position: 'relative',
      boxShadow: 8,
      transform: 'rotate(3deg)',
    }}
  >
    <Box
      sx={{
        bgcolor: link.color || TILE_COLORS[0],
        height: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {getFaviconUrl(link.url) ? (
        <Box
          component="img"
          src={getFaviconUrl(link.url)!}
          alt=""
          sx={{
            width: 48,
            height: 48,
            bgcolor: 'white',
            borderRadius: 1,
            p: 0.5,
          }}
        />
      ) : (
        <OpenInNewIcon sx={{ fontSize: 48, color: 'white' }} />
      )}
    </Box>
    <CardContent>
      <Typography variant="h6" noWrap>
        {link.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap>
        {new URL(link.url).hostname}
      </Typography>
    </CardContent>
  </Card>
);

const QuickLinks: React.FC = () => {
  const [links, setLinks] = useState<QuickLink[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<QuickLink | null>(null);
  const [viewingLink, setViewingLink] = useState<QuickLink | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuLinkId, setMenuLinkId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Form State
  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState(TILE_COLORS[0]);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Links aus localStorage laden
  useEffect(() => {
    const savedLinks = localStorage.getItem(STORAGE_KEY);
    if (savedLinks) {
      try {
        setLinks(JSON.parse(savedLinks));
      } catch {
        console.error('Fehler beim Laden der QuickLinks');
      }
    }
  }, []);

  // Links im localStorage speichern
  const saveLinks = (newLinks: QuickLink[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newLinks));
    setLinks(newLinks);
  };

  // Drag Handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = links.findIndex((l) => l.id === active.id);
      const newIndex = links.findIndex((l) => l.id === over.id);
      const newLinks = arrayMove(links, oldIndex, newIndex);
      saveLinks(newLinks);
      setSnackbar({ open: true, message: 'Reihenfolge gespeichert', severity: 'success' });
    }
  };

  // Dialog oeffnen fuer neuen Link
  const handleAddClick = () => {
    setEditingLink(null);
    setFormName('');
    setFormUrl('https://');
    setFormDescription('');
    setFormColor(TILE_COLORS[Math.floor(Math.random() * TILE_COLORS.length)]);
    setEditDialogOpen(true);
  };

  // Dialog oeffnen fuer Bearbeitung
  const handleEditClick = (link: QuickLink) => {
    setEditingLink(link);
    setFormName(link.name);
    setFormUrl(link.url);
    setFormDescription(link.description || '');
    setFormColor(link.color || TILE_COLORS[0]);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  // Link speichern
  const handleSave = () => {
    if (!formName.trim() || !formUrl.trim()) {
      setSnackbar({ open: true, message: 'Name und URL sind erforderlich', severity: 'error' });
      return;
    }

    // URL validieren
    try {
      new URL(formUrl);
    } catch {
      setSnackbar({ open: true, message: 'Ungueltige URL', severity: 'error' });
      return;
    }

    if (editingLink) {
      // Bestehenden Link aktualisieren
      const updatedLinks = links.map((l) =>
        l.id === editingLink.id
          ? { ...l, name: formName.trim(), url: formUrl.trim(), description: formDescription.trim(), color: formColor }
          : l
      );
      saveLinks(updatedLinks);
      setSnackbar({ open: true, message: 'Link aktualisiert', severity: 'success' });
    } else {
      // Neuen Link erstellen
      const newLink: QuickLink = {
        id: Date.now().toString(),
        name: formName.trim(),
        url: formUrl.trim(),
        description: formDescription.trim(),
        color: formColor,
      };
      saveLinks([...links, newLink]);
      setSnackbar({ open: true, message: 'Link hinzugefuegt', severity: 'success' });
    }

    setEditDialogOpen(false);
  };

  // Link loeschen
  const handleDelete = (id: string) => {
    const updatedLinks = links.filter((l) => l.id !== id);
    saveLinks(updatedLinks);
    setSnackbar({ open: true, message: 'Link geloescht', severity: 'success' });
    handleMenuClose();
  };

  // Link anzeigen
  const handleViewClick = (link: QuickLink) => {
    if (activeId) return; // Don't open while dragging
    setViewingLink(link);
    setIframeLoading(true);
    setIframeError(false);
    setViewDialogOpen(true);
  };

  // In neuem Tab oeffnen
  const handleOpenInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  // Menu Handling
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, linkId: string) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuLinkId(linkId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuLinkId(null);
  };

  // Fullscreen Toggle
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Iframe neu laden
  const handleRefreshIframe = () => {
    setIframeLoading(true);
    setIframeError(false);
    const iframe = document.getElementById('quicklink-iframe') as HTMLIFrameElement;
    if (iframe && viewingLink) {
      iframe.src = viewingLink.url + (viewingLink.url.includes('?') ? '&' : '?') + '_t=' + Date.now();
    }
  };

  const activeLink = activeId ? links.find((l) => l.id === activeId) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Quick Links</Typography>
          <Typography variant="body2" color="text.secondary">
            Kacheln per Drag & Drop verschieben
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
          Neuer Link
        </Button>
      </Box>

      {links.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Keine Quick Links vorhanden
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Fuegen Sie Ihre Lieblings-Webseiten als Quick Links hinzu
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddClick}>
            Ersten Link hinzufuegen
          </Button>
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={links.map((l) => l.id)} strategy={rectSortingStrategy}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
              }}
            >
              {links.map((link) => (
                <SortableLinkCard
                  key={link.id}
                  link={link}
                  onView={handleViewClick}
                  onMenuClick={handleMenuClick}
                  isDragging={activeId === link.id}
                />
              ))}

              {/* Add Button als Kachel */}
              <Box
                sx={{
                  width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)', lg: 'calc(25% - 18px)' },
                  minWidth: 250,
                }}
              >
                <Card
                  sx={{
                    height: '100%',
                    minHeight: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={handleAddClick}
                >
                  <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    <AddIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography>Neuen Link hinzufuegen</Typography>
                  </Box>
                </Card>
              </Box>
            </Box>
          </SortableContext>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeLink ? <DragOverlayCard link={activeLink} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={() => menuLinkId && handleOpenInNewTab(links.find((l) => l.id === menuLinkId)?.url || '')}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>In neuem Tab oeffnen</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuLinkId && handleEditClick(links.find((l) => l.id === menuLinkId)!)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Bearbeiten</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuLinkId && handleDelete(menuLinkId)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Loeschen</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingLink ? 'Link bearbeiten' : 'Neuer Quick Link'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            value={formUrl}
            onChange={(e) => setFormUrl(e.target.value)}
            placeholder="https://example.com"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Beschreibung (optional)"
            fullWidth
            multiline
            rows={2}
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom>
            Farbe
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {TILE_COLORS.map((color) => (
              <Box
                key={color}
                onClick={() => setFormColor(color)}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: color,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: formColor === color ? '3px solid' : '2px solid transparent',
                  borderColor: formColor === color ? 'primary.main' : 'transparent',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog (iframe) */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth={false}
        fullScreen={isFullscreen}
        PaperProps={{
          sx: isFullscreen
            ? {}
            : {
                width: '90vw',
                height: '85vh',
                maxWidth: 'none',
              },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {viewingLink && getFaviconUrl(viewingLink.url) && (
              <Box
                component="img"
                src={getFaviconUrl(viewingLink.url)!}
                alt=""
                sx={{ width: 24, height: 24 }}
              />
            )}
            <Typography variant="h6">{viewingLink?.name}</Typography>
          </Box>
          <Box>
            <Tooltip title="Neu laden">
              <IconButton onClick={handleRefreshIframe} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="In neuem Tab oeffnen">
              <IconButton onClick={() => viewingLink && handleOpenInNewTab(viewingLink.url)} size="small">
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}>
              <IconButton onClick={toggleFullscreen} size="small">
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Schliessen">
              <IconButton onClick={() => setViewDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {iframeLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {iframeError ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
              }}
            >
              <Alert severity="warning" sx={{ mb: 2 }}>
                Diese Webseite kann nicht in einem Frame angezeigt werden (X-Frame-Options).
              </Alert>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={() => viewingLink && handleOpenInNewTab(viewingLink.url)}
              >
                In neuem Tab oeffnen
              </Button>
            </Box>
          ) : (
            viewingLink && (
              <iframe
                id="quicklink-iframe"
                src={viewingLink.url}
                title={viewingLink.name}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: iframeLoading ? 'none' : 'block',
                }}
                onLoad={() => setIframeLoading(false)}
                onError={() => {
                  setIframeLoading(false);
                  setIframeError(true);
                }}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
              />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuickLinks;
